import { useMemo } from "react";

import { DateTime } from "luxon";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";
import type {
	ChatModel,
	ConversationInfo,
	ConversationSubjects,
	PaginationParamsModel,
	PaginationTokenType,
	PostAttachmentModel
} from "shared/types";
import { chatResolver, conversationsResolver, paginationTokenResolver } from "shared/types";

import BaseConverter from "./converters/baseConverter";

import type { EntityMessagingConversationRoles } from "../types/FirebaseUserRole";
import type { ConversationDetailsInfo, ConversationParticipant, ConversationsSettings } from "../types/Messages";
import { chatDetailsResolver } from "../types/Messages";

interface MessageActionReturnType {
	conversation: {
		id: string;
	};
}

const useMessagingApiService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			getConversation: async (params: { id?: string; subject?: ConversationSubjects }) => {
				return BaseConverter.convert<{ conversation: ConversationInfo }>(
					await actions.post(API_URLS.MESSAGING.GET_CONVERSATION, params, {
						resolvers: [
							{
								resolver: chatResolver,
								fields: ["conversation"]
							}
						]
					})
				);
			},
			getConversationDetails: async (id: string) => {
				return BaseConverter.convert<{ conversation: ConversationDetailsInfo }>(
					await actions.get(
						API_URLS.MESSAGING.GET_CONVERSATION_DETAILS,
						{
							id
						},
						{
							resolvers: [
								{
									resolver: chatDetailsResolver,
									fields: ["conversation"]
								}
							]
						}
					)
				);
			},
			getConversationsList: async (params: { search?: string } & PaginationParamsModel) => {
				return BaseConverter.convert<{
					conversations: ChatModel[];
					nextToken: PaginationTokenType;
				}>(
					await actions.get(API_URLS.MESSAGING.GET_CONVERSATIONS, params, {
						resolvers: [
							{
								resolver: conversationsResolver,
								fields: ["conversations"]
							},
							{
								fields: ["nextToken"],
								resolver: paginationTokenResolver
							}
						]
					})
				);
			},
			sendMessage: async ({
				conversationId,
				message,
				attachments
			}: {
				conversationId: string;
				message: {
					text: string;
					tag: string;
				};
				attachments?: PostAttachmentModel[];
			}) => {
				const sentAt = DateTime.now()
					.plus({ seconds: 1 })
					.set({
						millisecond: 0
					})
					.toUTC()
					.toISO({ suppressMilliseconds: true, includeOffset: false });

				return BaseConverter.convert<{
					conversation: {
						id: string;
					};
					message: {
						id: string;
						tag: string;
					};
				}>(
					await actions.post(API_URLS.MESSAGING.SEND_MESSAGE, {
						conversationId,
						message: {
							id: message.tag,
							tag: message.tag,
							sentAt,
							body: {
								text: message.text
							},
							attachments: attachments?.map(attachment => ({
								entity: attachment.entity,
								kind: attachment.kind
							}))
						}
					})
				);
			},
			updateMessage: async (conversationId: string, id: string, updatedText: string) => {
				return BaseConverter.convert(
					await actions.put(API_URLS.MESSAGING.UPDATE_MESSAGE, {
						conversationId,
						message: {
							id,
							body: {
								text: updatedText
							}
						}
					})
				);
			},
			updateConversation: async (params: {
				conversationId: string;
				title?: {
					text?: string;
					image?: null | string;
				};
				mute?: boolean;
				pin?: boolean;
				updateLastOpened?: boolean;
				settings?: ConversationsSettings;
			}) => {
				return BaseConverter.convert<{ conversation: ConversationInfo }>(
					await actions.put(API_URLS.MESSAGING.UPDATE_CONVERSATION, params)
				);
			},
			updateConversationParticipants: async (params: {
				conversationId: string;
				participants: {
					add?: {
						id: string;
						role: EntityMessagingConversationRoles;
					}[];
					remove?: {
						id: string;
						role?: EntityMessagingConversationRoles;
					}[];
				};
			}) => {
				return BaseConverter.convert<MessageActionReturnType>(
					await actions.put(API_URLS.MESSAGING.UPDATE_CONVERSATION_PARTICIPANTS, params)
				);
			},
			listConversationParticipants: async (conversationId: string) => {
				return BaseConverter.convert<{ conversation: { id: string; participants: ConversationParticipant[] } }>(
					await actions.get(API_URLS.MESSAGING.LIST_CONVERSATION_PARTICIPANTS, {
						conversationId
					})
				);
			},
			deleteMessage: async (conversationId: string, messageId: string) => {
				return BaseConverter.convert<MessageActionReturnType>(
					await actions.post(API_URLS.MESSAGING.DELETE_MESSAGE, {
						conversationId,
						messageId
					})
				);
			}
		}),
		[actions]
	);
};

export default useMessagingApiService;
